import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Phone, Mail, RefreshCw, Search, Sparkles, Calculator, Eye, MessageCircle, FileText, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, clearAdminToken, getAdminToken } from "@/lib/api";

const STATUS = [
  { v: "new", l: "New" },
  { v: "contacted", l: "Contacted" },
  { v: "in_progress", l: "In Progress" },
  { v: "closed_won", l: "Won" },
  { v: "closed_lost", l: "Lost" },
];
const STATUS_TONE = {
  new: "bg-brand-terracotta/10 text-brand-terracotta border-brand-terracotta/30",
  contacted: "bg-brand-gold/15 text-[#9c6a18] border-brand-gold/40",
  in_progress: "bg-brand-forest/10 text-brand-forest border-brand-forest/25",
  closed_won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed_lost: "bg-rose-100 text-rose-700 border-rose-200",
};
const LOAN = {
  home_loan: "Home Loan",
  business_loan: "Business Loan",
  lap_lrd: "LAP / LRD",
  project_finance: "Project Finance",
  personal_loan: "Personal Loan",
  balance_transfer: "Balance Transfer",
  other: "Other",
};
const EMP = {
  salaried: "Salaried",
  self_employed: "Self-Employed",
  business_owner: "Business Owner",
  property_investor: "Property Investor",
  other: "Other",
};

// Apply-method metadata — matches backend ApplyMethod literal
const METHOD = {
  form: {
    label: "Form",
    icon: FileText,
    chip: "bg-brand-forest/8 text-brand-forest border-brand-forest/25",
    dot: "bg-brand-forest",
  },
  chat: {
    label: "Chat",
    icon: MessageSquare,
    chip: "bg-brand-terracotta/10 text-brand-terracotta border-brand-terracotta/30",
    dot: "bg-brand-terracotta",
  },
  whatsapp_redirect: {
    label: "WhatsApp",
    icon: MessageCircle,
    chip: "bg-[#25D366]/12 text-[#176a37] border-[#25D366]/40",
    dot: "bg-[#25D366]",
  },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, closed_won: 0, by_method: { form: 0, chat: 0, whatsapp_redirect: 0 } });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/inquiries"), api.get("/admin/stats")]);
      setRows(a.data);
      setStats(b.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
        return;
      }
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, value) => {
    try {
      await api.patch(`/inquiries/${id}/status`, { status: value });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status: value } : x)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => {
    clearAdminToken();
    navigate("/admin", { replace: true });
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      const blob = `${r.name} ${r.email} ${r.phone} ${LOAN[r.loan_type] || ""} ${r.city || ""} ${METHOD[r.apply_method || "form"]?.label || ""}`.toLowerCase();
      return blob.includes(q.toLowerCase());
    });
  }, [rows, q, statusFilter]);

  return (
    <div data-testid="admin-dashboard-page" className="container-x py-10 md:py-14">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="eyebrow">Admin · Control Room</span>
          <h1 className="mt-3 h-display text-3xl sm:text-4xl">Loan Inquiries</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/eligibility" data-testid="admin-eligibility-link" className="btn-ghost">
            <Calculator className="h-4 w-4" /> Eligibility
          </Link>
          <Link to="/admin/content" data-testid="admin-content-link" className="btn-ghost">
            <Sparkles className="h-4 w-4" /> Content Studio
          </Link>
          <button onClick={load} data-testid="admin-refresh" className="btn-ghost">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout" className="btn-secondary">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="stat-total" label="Total Inquiries" value={stats.total} />
        <Stat testid="stat-new" label="New" value={stats.new} accent />
        <Stat testid="stat-progress" label="In Progress" value={stats.in_progress} />
        <Stat testid="stat-won" label="Closed Won" value={stats.closed_won} good />
      </section>

      <MethodFunnel byMethod={stats.by_method} total={stats.total} />

      <section className="mt-8 card-soft p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-sage" />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone, type..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-filter" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link to="/" className="btn-ghost">View site</Link>
        </div>

        <div className="mt-5 overflow-x-auto">
          {loading ? (
            <div className="py-16 grid place-items-center text-brand-sage">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="py-16 text-center text-brand-sage">
              No inquiries match your filters yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-sage uppercase tracking-[0.14em] text-[11px]">
                  <th className="py-3 pr-4">Lead</th>
                  <th className="py-3 pr-4">Loan</th>
                  <th className="py-3 pr-4">Profile</th>
                  <th className="py-3 pr-4">Source</th>
                  <th className="py-3 pr-4">When</th>
                  <th className="py-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-t border-brand-sand align-top">
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-outfit font-semibold text-brand-forest">{r.name}</div>
                        {r.application_type === "full" && (
                          <Badge className="bg-brand-gold/20 text-[#9c6a18] border border-brand-gold/40 text-[9px] tracking-[0.12em] uppercase">Full</Badge>
                        )}
                        <MethodChip method={r.apply_method} />
                      </div>
                      <div className="mt-1 flex flex-col gap-1 text-xs text-brand-sage">
                        <a className="flex items-center gap-1.5 hover:text-brand-terracotta" href={`tel:${r.phone}`}>
                          <Phone className="h-3 w-3" /> {r.phone}
                        </a>
                        <a className="flex items-center gap-1.5 hover:text-brand-terracotta" href={`mailto:${r.email}`}>
                          <Mail className="h-3 w-3" /> {r.email}
                        </a>
                        <button
                          onClick={() => setDetail(r)}
                          data-testid={`admin-view-${r.id}`}
                          className="inline-flex items-center gap-1 text-brand-terracotta hover:text-[#b25535] mt-0.5 self-start"
                        >
                          <Eye className="h-3 w-3" /> View details
                        </button>
                      </div>
                    </td>
                    <td className="py-4 pr-4">
                      <div className="font-medium text-brand-forest">{LOAN[r.loan_type] || r.loan_type}</div>
                      <div className="text-xs text-brand-sage mt-1">{r.loan_amount || "—"}</div>
                      {r.message && (
                        <div className="text-xs text-brand-sage mt-1.5 line-clamp-2 max-w-[260px]">{r.message}</div>
                      )}
                    </td>
                    <td className="py-4 pr-4 text-brand-forest/85">
                      <div>{EMP[r.employment_type] || r.employment_type}</div>
                      <div className="text-xs text-brand-sage mt-1">{r.city || "—"}</div>
                    </td>
                    <td className="py-4 pr-4 text-xs">
                      {(r.persona || r.utm_source || r.landing_page) ? (
                        <div className="space-y-1">
                          {r.persona && (
                            <div>
                              <span className="rounded-full bg-brand-terracotta/10 text-brand-terracotta border border-brand-terracotta/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                                {String(r.persona).replace(/-/g, " ")}
                              </span>
                            </div>
                          )}
                          {r.utm_source && (
                            <div className="text-brand-forest font-medium">
                              {r.utm_source}
                              {r.utm_medium ? ` · ${r.utm_medium}` : ""}
                            </div>
                          )}
                          {r.utm_campaign && (
                            <div className="text-brand-sage truncate max-w-[180px]" title={r.utm_campaign}>{r.utm_campaign}</div>
                          )}
                          {!r.utm_source && r.landing_page && (
                            <div className="text-brand-sage truncate max-w-[180px]" title={r.landing_page}>{r.landing_page}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-brand-sage">Direct</span>
                      )}
                    </td>
                    <td className="py-4 pr-4 text-brand-sage text-xs">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="py-4 pr-4">
                      <div className="flex flex-col gap-2">
                        <Badge className={`border ${STATUS_TONE[r.status] || ""} font-medium`}>
                          {STATUS.find((s) => s.v === r.status)?.l || r.status}
                        </Badge>
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger data-testid={`admin-status-${r.id}`} className="h-8 w-[150px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS.map((s) => (
                              <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <LeadDetailDialog row={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

const fmtINR = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₹${new Intl.NumberFormat("en-IN").format(Math.round(+n || 0))}`;

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-brand-terracotta font-semibold mb-2">{title}</div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">{children}</dl>
    </div>
  );
}
function KV({ k, v }) {
  if (v === null || v === undefined || v === "") return null;
  const display = v === true ? "Yes" : v === false ? "No" : String(v);
  return (
    <div className="flex justify-between gap-3 border-b border-brand-sand/70 py-1.5">
      <dt className="text-brand-sage">{k}</dt>
      <dd className="font-medium text-brand-forest text-right break-all max-w-[55%]">{display}</dd>
    </div>
  );
}

function LeadDetailDialog({ row, onClose }) {
  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-detail-dialog" className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-2xl">{row.name}</DialogTitle>
          <DialogDescription>
            {LOAN[row.loan_type] || row.loan_type}
            {row.loan_amount ? ` · ${row.loan_amount}` : ""}
            {row.application_type === "full" ? " · Full Application" : " · Quick Inquiry"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <Section title="Contact">
            <KV k="Phone" v={row.phone} />
            <KV k="Email" v={row.email} />
            <KV k="City" v={row.city} />
            <KV k="PIN code" v={row.pincode} />
            <KV k="DOB" v={row.dob} />
            <KV k="Gender" v={row.gender} />
            <KV k="PAN" v={row.pan} />
            <KV k="Residence" v={row.residence_type} />
            <KV k="Years at address" v={row.years_at_current} />
          </Section>

          <Section title="Loan need">
            <KV k="Loan type" v={LOAN[row.loan_type] || row.loan_type} />
            <KV k="Amount sought" v={row.loan_amount} />
            <KV k="Tenure (months)" v={row.tenure_months_requested} />
            <KV k="Purpose" v={row.loan_purpose} />
          </Section>

          <Section title="Income & employment">
            <KV k="Employment" v={EMP[row.employment_type] || row.employment_type} />
            <KV k="Employer" v={row.company_name} />
            <KV k="Designation" v={row.designation} />
            <KV k="Work exp (yrs)" v={row.work_exp_years} />
            <KV k="Net take-home / mo" v={fmtINR(row.net_monthly_takehome)} />
            <KV k="Salary mode" v={row.salary_mode} />
            <KV k="Business name" v={row.business_name} />
            <KV k="Vintage (yrs)" v={row.business_vintage_years} />
            <KV k="Annual turnover" v={fmtINR(row.annual_turnover)} />
            <KV k="GST registered" v={row.gst_registered} />
            <KV k="GSTIN" v={row.gstin} />
          </Section>

          <Section title="Liabilities & collateral">
            <KV k="Existing EMIs / mo" v={fmtINR(row.existing_emis_monthly)} />
            <KV k="Existing loans" v={row.existing_loans_text} />
            <KV k="CC limit" v={fmtINR(row.credit_card_limit_total)} />
            <KV k="CC outstanding" v={fmtINR(row.credit_card_outstanding)} />
            <KV k="Bureau score" v={row.bureau_score_known} />
            <KV k="Has collateral" v={row.has_property_collateral} />
            <KV k="Property value" v={fmtINR(row.property_value)} />
            <KV k="Property city" v={row.property_city} />
          </Section>

          {(row.consent_credit_pull !== null && row.consent_credit_pull !== undefined) ||
          (row.consent_terms !== null && row.consent_terms !== undefined) ? (
            <Section title="Consents (for compliance)">
              <KV k="Credit-bureau verification" v={row.consent_credit_pull} />
              <KV k="Terms accepted" v={row.consent_terms} />
            </Section>
          ) : null}

          <Section title="Attribution">
            <KV k="Applied via" v={METHOD[row.apply_method || "form"]?.label} />
            <KV k="Persona" v={row.persona} />
            <KV k="Landing page" v={row.landing_page} />
            <KV k="UTM source" v={row.utm_source} />
            <KV k="UTM medium" v={row.utm_medium} />
            <KV k="UTM campaign" v={row.utm_campaign} />
            <KV k="UTM content" v={row.utm_content} />
            <KV k="UTM term" v={row.utm_term} />
            <KV k="Referrer URL" v={row.referrer} />
            <KV k="Referrer (name)" v={row.referrer_name} />
            <KV k="Referrer (phone)" v={row.referrer_phone} />
            <KV k="Submitted" v={row.created_at ? new Date(row.created_at).toLocaleString() : null} />
          </Section>

          {row.message && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-brand-terracotta font-semibold mb-2">Notes from applicant</div>
              <p className="text-sm text-brand-forest/85 whitespace-pre-line bg-brand-sand/40 rounded-xl p-4 border border-brand-sand">{row.message}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <a href={`tel:${row.phone}`} className="btn-primary text-sm"><Phone className="h-4 w-4" /> Call</a>
            <a href={`mailto:${row.email}`} className="btn-secondary text-sm"><Mail className="h-4 w-4" /> Email</a>
            <a
              href={`https://wa.me/${(row.phone || "").replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1ea854]"
            >
              <Phone className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, good, testid }) {
  return (
    <div data-testid={testid} className={`card-soft p-5 ${accent ? "ring-1 ring-brand-terracotta/30" : ""}`}>
      <div className="text-xs uppercase tracking-[0.16em] text-brand-sage">{label}</div>
      <div className={`mt-2 font-outfit font-extrabold text-3xl ${good ? "text-emerald-700" : accent ? "text-brand-terracotta" : "text-brand-forest"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

function MethodChip({ method }) {
  const m = METHOD[method || "form"] || METHOD.form;
  const Icon = m.icon;
  return (
    <span
      data-testid={`method-chip-${method || "form"}`}
      className={`inline-flex items-center gap-1 rounded-full border ${m.chip} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]`}
    >
      <Icon className="h-2.5 w-2.5" /> {m.label}
    </span>
  );
}

function MethodFunnel({ byMethod, total }) {
  const bm = byMethod || { form: 0, chat: 0, whatsapp_redirect: 0 };
  const safeTotal = Math.max(total || 0, bm.form + bm.chat + bm.whatsapp_redirect, 1);
  const order = ["form", "chat", "whatsapp_redirect"];
  return (
    <section data-testid="admin-method-funnel" className="mt-5 card-soft p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow">How leads applied</div>
          <div className="mt-1 text-xs text-brand-sage">Which path prospects chose — form, chat, or WhatsApp.</div>
        </div>
        <div className="text-xs text-brand-sage hidden sm:block">
          Based on {safeTotal} lead{safeTotal === 1 ? "" : "s"}
        </div>
      </div>

      {/* Stacked progress bar */}
      <div className="relative h-3 rounded-full bg-brand-sand/60 overflow-hidden flex">
        {order.map((k) => {
          const w = ((bm[k] || 0) / safeTotal) * 100;
          if (w <= 0) return null;
          return (
            <div
              key={k}
              data-testid={`method-bar-${k}`}
              className={`${METHOD[k].dot} h-full transition-all duration-500`}
              style={{ width: `${w}%` }}
              title={`${METHOD[k].label}: ${bm[k]}`}
            />
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {order.map((k) => {
          const n = bm[k] || 0;
          const pct = Math.round(((n) / safeTotal) * 100);
          const Icon = METHOD[k].icon;
          return (
            <div
              key={k}
              data-testid={`method-count-${k}`}
              className="flex items-center gap-3 rounded-xl border border-brand-sand bg-white px-3 py-2.5"
            >
              <span className={`h-8 w-8 shrink-0 grid place-items-center rounded-lg ${METHOD[k].dot} text-white`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-brand-sage">{METHOD[k].label}</div>
                <div className="font-outfit font-extrabold text-xl text-brand-forest leading-none mt-0.5">
                  {n}
                  <span className="ml-1 text-[11px] font-medium text-brand-sage">· {pct}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
